import { ActionIcon } from "./view-models/utils";
import {
  ActionViewModel,
  ActionViewModelImplementation,
  Effector,
  effectorToMapKey,
} from "./view-models/ActionViewModel";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { CarbonUser } from "common/utils/auth";
import { ConfirmationDialog } from "common/components/ConfirmationDialog";
import { HitchType } from "protos/atk/enums";
import {
  hitchTypeToLocaleString,
  ptoTypeToNameString,
} from "common/utils/enumTranslations";
import {
  RigActivitiesFormViewModel,
  RigActivityConfigViewModel,
} from "../RigActivitiesFormViewModel";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { TypedPathTo, useFormikValuesAtBasePath } from "portal/utils/typedPath";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { validActionsForEffector } from "./view-models/factory";
import lodashGet from "lodash/get";
import MenuArrowLeftIcon from "@mui/icons-material/NavigateBefore";
import MenuArrowRightIcon from "@mui/icons-material/NavigateNext";
import React, { useCallback, useMemo, useState } from "react";

type SubmenuData = {
  buttonLabel: string;
  buttonDescription: string;
  title: string;
  subtitle: string;
  effector: Effector;
  blockedBy: ActionViewModel<unknown> | undefined;
};
type SubmenuSection = {
  sectionTitle: string;
  submenus: SubmenuData[];
};

function listSubmenuSections(
  t: TFunction,
  user: CarbonUser | undefined,
  rigEffectiveConfig: RigEffectiveConfig,
  activityConfig: RigActivityConfigViewModel,
  creatingInStageIndex: number
): SubmenuSection[] {
  const submenus: SubmenuSection[] = [];

  // identify actions which can be blocking one or more submenus from being
  // chosen
  const possiblyBlockingActions: ActionViewModel<unknown>[] =
    activityConfig.actions.filter(
      (action) =>
        action.startInStageIndex === creatingInStageIndex ||
        (action.startInStageIndex < creatingInStageIndex &&
          action.holdingAction)
    );

  const tractorSection: SubmenuSection = {
    sectionTitle: "tractor",
    submenus: [
      {
        buttonLabel: t(
          "views.autotractor.autonomousActivities.action.newMenu.engineRpm.title"
        ),
        buttonDescription: t(
          "views.autotractor.autonomousActivities.action.newMenu.engineRpm.buttonDescription"
        ),
        title: t(
          "views.autotractor.autonomousActivities.action.newMenu.engineRpm.title"
        ),
        subtitle: t(
          "views.autotractor.autonomousActivities.action.newMenu.engineRpm.subtitle"
        ),
        effector: { type: "engine-rpm" },
        blockedBy: possiblyBlockingActions.find(
          (action) => action.effector.type === "engine-rpm"
        ),
      },
    ],
  };

  if (
    validActionsForEffector(user, { type: "advanced" }, rigEffectiveConfig)
      .length > 0
  ) {
    tractorSection.submenus.push({
      buttonLabel: t(
        "views.autotractor.autonomousActivities.action.newMenu.advanced.title"
      ),
      buttonDescription: t(
        "views.autotractor.autonomousActivities.action.newMenu.advanced.buttonDescription"
      ),
      title: t(
        "views.autotractor.autonomousActivities.action.newMenu.advanced.title"
      ),
      subtitle: t(
        "views.autotractor.autonomousActivities.action.newMenu.advanced.subtitle"
      ),
      effector: { type: "advanced" },
      blockedBy: possiblyBlockingActions.find(
        (action) => action.effector.type === "advanced"
      ),
    });
  }

  for (const pto of rigEffectiveConfig.ptos) {
    tractorSection.submenus.push({
      buttonLabel: ptoTypeToNameString(t, pto.ptoType),
      buttonDescription: t(
        "views.autotractor.autonomousActivities.action.newMenu.pto.buttonDescription"
      ),
      title: ptoTypeToNameString(t, pto.ptoType),
      subtitle: t(
        "views.autotractor.autonomousActivities.action.newMenu.pto.subtitle"
      ),
      effector: { type: "pto", ptoType: pto.ptoType },
      blockedBy: possiblyBlockingActions.find(
        (action) =>
          action.effector.type === "pto" &&
          action.effector.ptoType === pto.ptoType
      ),
    });
  }

  submenus.push(tractorSection);

  const hitchSections = new Map<HitchType, SubmenuSection>();
  const implementSections: SubmenuSection[] = [];

  for (const implement of rigEffectiveConfig.implements) {
    let hitchSection: SubmenuSection | undefined;

    if (
      implement.activeHitch &&
      validActionsForEffector(
        user,
        {
          type: "hitch",
          hitchType: implement.activeHitch.type,
        },
        rigEffectiveConfig
      ).length > 0
    ) {
      hitchSection = hitchSections.getOrInsertComputed(
        implement.activeHitch.type,
        (hitchType) => ({
          sectionTitle: t(hitchTypeToLocaleString(hitchType)),
          submenus: [
            {
              buttonLabel: t(
                "views.autotractor.autonomousActivities.action.newMenu.hitchHeight.title"
              ),
              buttonDescription: t(
                "views.autotractor.autonomousActivities.action.newMenu.hitchHeight.buttonDescription"
              ),
              title: t(
                "views.autotractor.autonomousActivities.action.newMenu.hitchHeight.title"
              ),
              subtitle: t(
                "views.autotractor.autonomousActivities.action.newMenu.hitchHeight.subtitle"
              ),
              effector: {
                type: "hitch",
                hitchType,
              },
              blockedBy: possiblyBlockingActions.find(
                (action) =>
                  action.effector.type === "hitch" &&
                  action.effector.hitchType === hitchType
              ),
            },
          ],
        })
      );
    }

    const implementSection: SubmenuSection = {
      sectionTitle: implement.customerProvidedName,
      submenus: [],
    };

    if (
      validActionsForEffector(
        user,
        { type: "laserweeder", implementId: implement.implementId },
        rigEffectiveConfig
      ).length > 0
    ) {
      implementSection.submenus.push({
        buttonLabel: t(
          "views.autotractor.autonomousActivities.action.newMenu.laserweeder.title"
        ),
        buttonDescription: t(
          "views.autotractor.autonomousActivities.action.newMenu.laserweeder.buttonDescription"
        ),
        title: t(
          "views.autotractor.autonomousActivities.action.newMenu.laserweeder.title"
        ),
        subtitle: t(
          "views.autotractor.autonomousActivities.action.newMenu.laserweeder.subtitle"
        ),
        effector: {
          type: "laserweeder",
          implementId: implement.implementId,
        },
        blockedBy: possiblyBlockingActions.find(
          (action) =>
            action.effector.type === "laserweeder" &&
            action.effector.implementId === implement.implementId
        ),
      });
    }

    if (
      validActionsForEffector(
        user,
        { type: "lw-estop", implementId: implement.implementId },
        rigEffectiveConfig
      ).length > 0
    ) {
      implementSection.submenus.push({
        buttonLabel: t(
          "views.autotractor.autonomousActivities.action.newMenu.lwEstop.title"
        ),
        buttonDescription: t(
          "views.autotractor.autonomousActivities.action.newMenu.lwEstop.buttonDescription"
        ),
        title: t(
          "views.autotractor.autonomousActivities.action.newMenu.lwEstop.title"
        ),
        subtitle: t(
          "views.autotractor.autonomousActivities.action.newMenu.lwEstop.subtitle"
        ),
        effector: {
          type: "lw-estop",
          implementId: implement.implementId,
        },
        blockedBy: possiblyBlockingActions.find(
          (action) =>
            action.effector.type === "lw-estop" &&
            action.effector.implementId === implement.implementId
        ),
      });
    }

    for (const scv of rigEffectiveConfig.scvs) {
      if (scv.implementId === implement.implementId) {
        if (!scv.input) {
          // it's a legacy SCV toplink, we can't create actions for it anyway
          continue;
        }

        const scvSubmenu: SubmenuData = {
          buttonLabel: scv.input.function,
          buttonDescription: t(
            "views.autotractor.autonomousActivities.action.newMenu.scv.buttonDescription",
            { function: scv.input.function }
          ),
          title: scv.input.function,
          subtitle: t(
            "views.autotractor.autonomousActivities.action.newMenu.scv.subtitle"
          ),
          effector: { type: "scv", scvInputId: scv.input.id },
          blockedBy: possiblyBlockingActions.find(
            (action) =>
              action.effector.type === "scv" &&
              action.effector.scvInputId === scv.input!.id
          ),
        };

        if (scv.input.isHydraulicTopLink) {
          // top links get special handling and live with the hitch they
          // connect the implement to
          if (hitchSection) {
            hitchSection.submenus.push(scvSubmenu);
          }
        } else {
          implementSection.submenus.push(scvSubmenu);
        }
      }
    }

    if (implementSection.submenus.length > 0) {
      implementSections.push(implementSection);
    }
  }

  submenus.push(...hitchSections.values(), ...implementSections.values());

  return submenus;
}

const NewActionChooserTopLevelMenu: React.FC<{
  submenus: SubmenuSection[];
  onOpenSubmenu: (submenu: SubmenuData) => void;
}> = ({ submenus, onOpenSubmenu }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      {submenus.map((section) => (
        <>
          <Typography variant="body1" className="py-2">
            {section.sectionTitle}
          </Typography>
          {section.submenus.map((submenu) => (
            <Button
              key={JSON.stringify(submenu.effector)}
              variant="outlined"
              color="inherit"
              onClick={() => onOpenSubmenu(submenu)}
              disabled={submenu.blockedBy !== undefined}
              className="grid grid-cols-[minmax(0,1fr)_min-content] normal-case text-left"
            >
              <Typography variant="body1">{submenu.title}</Typography>
              <Typography variant="body2">
                {submenu.blockedBy
                  ? submenu.blockedBy.effectorBlockedByActionMessage(t)
                  : submenu.buttonDescription}
              </Typography>
              <MenuArrowRightIcon className="col-start-2 row-span-full" />
            </Button>
          ))}
        </>
      ))}
    </div>
  );
};

const NewActionChooserSubmenu: React.FC<{
  stageIndex: number;
  submenu: SubmenuData;
  onDone: (viewModel: ActionViewModel<unknown>) => void;
  activityBasePath: TypedPathTo<RigActivityConfigViewModel>;
}> = ({ stageIndex, submenu, onDone, activityBasePath }) => {
  const { t } = useTranslation();
  const { user } = useSelf();
  const { values } = useFormikContext<RigActivitiesFormViewModel>();
  const activityState = useFormikValuesAtBasePath(activityBasePath);
  const [confirmingHoldingActionImpl, setConfirmingHoldingActionImpl] =
    useState<ActionViewModelImplementation<unknown> | undefined>(undefined);

  // collect relevant actions by submenu section
  const actions = useMemo(
    () =>
      validActionsForEffector(
        user,
        submenu.effector,
        values.rigEffectiveConfig
      ),
    [submenu.effector, user, values.rigEffectiveConfig]
  );
  const quickActions = actions.filter(
    (action) => action.actionMenuSection === "quick-action"
  );
  const holdingActions = actions.filter(
    (action) => action.actionMenuSection === "holding-action"
  );

  const handleChoose = useCallback(
    (newActionImpl: ActionViewModelImplementation<unknown>) => {
      // is the user creating a holding action?  check for any other actions
      // starting after it, and if there are any present, issue a warning that
      // they'll be evicted
      if (
        newActionImpl.actionMenuSection === "holding-action" &&
        activityState.actions.some(
          (action) =>
            effectorToMapKey(action.effector) ===
              effectorToMapKey(submenu.effector) &&
            action.startInStageIndex >= stageIndex
        )
      ) {
        setConfirmingHoldingActionImpl(newActionImpl);
        return;
      }

      onDone(newActionImpl.newAction(submenu.effector, stageIndex));
    },
    [activityState.actions, submenu.effector, stageIndex, onDone]
  );

  let confirmHoldingActionBody: JSX.Element | undefined;

  if (confirmingHoldingActionImpl) {
    if (submenu.effector.type === "scv" || submenu.effector.type === "hitch") {
      confirmHoldingActionBody = (
        <Typography>
          <Trans
            i18nKey="views.autotractor.autonomousActivities.action.confirmHoldingAction.hydraulic"
            values={{
              title: submenu.title,
              action: t(confirmingHoldingActionImpl.actionButtonLabelI18nKey),
              stageIndex: stageIndex + 1,
            }}
          />
        </Typography>
      );
    } else {
      confirmHoldingActionBody = (
        <Typography>
          <Trans
            i18nKey="views.autotractor.autonomousActivities.action.confirmHoldingAction.other"
            values={{
              title: submenu.title,
              action: t(confirmingHoldingActionImpl.actionButtonLabelI18nKey),
              stageIndex: stageIndex + 1,
            }}
          />
        </Typography>
      );
    }
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {confirmingHoldingActionImpl && (
        <ConfirmationDialog
          title={t(
            "views.autotractor.autonomousActivities.action.confirmHoldingAction.title"
          )}
          onClose={() => setConfirmingHoldingActionImpl(undefined)}
          onYes={() =>
            onDone(
              confirmingHoldingActionImpl.newAction(
                submenu.effector,
                stageIndex
              )
            )
          }
          description={confirmHoldingActionBody}
          noText={t("utils.actions.cancel")}
          yesText={t("utils.actions.replace")}
        />
      )}

      {quickActions.length > 0 && (
        <>
          <Typography variant="h6" className="col-span-full">
            {t(
              "views.autotractor.autonomousActivities.action.newMenu.quickSubmenuTitle"
            )}
          </Typography>
          <Typography className="col-span-full">
            <Trans i18nKey="views.autotractor.autonomousActivities.action.newMenu.quickSubmenuSubtitle" />
          </Typography>
          {quickActions.map((action) => (
            <Button
              variant="atk-new-action-selection"
              color="inherit"
              key={action.actionButtonLabelI18nKey}
              onClick={() => handleChoose(action)}
              className="flex flex-col gap-1"
            >
              <ActionIcon
                Icon={action.components.Icon}
                isHoldingAction={false}
              />
              {t(action.actionButtonLabelI18nKey)}
            </Button>
          ))}
        </>
      )}

      {holdingActions.length > 0 && (
        <>
          <Typography variant="h6" className="col-span-full">
            {t(
              "views.autotractor.autonomousActivities.action.newMenu.holdingSubmenuTitle"
            )}
          </Typography>
          <Typography className="col-span-full">
            <Trans i18nKey="views.autotractor.autonomousActivities.action.newMenu.holdingSubmenuSubtitle" />
          </Typography>
          {holdingActions.map((action) => (
            <Button
              variant="atk-new-action-selection"
              color="inherit"
              key={action.actionButtonLabelI18nKey}
              onClick={() => handleChoose(action)}
              className="flex flex-col gap-1"
            >
              <ActionIcon
                Icon={action.components.Icon}
                isHoldingAction={true}
              />
              {t(action.actionButtonLabelI18nKey)}
            </Button>
          ))}
        </>
      )}
    </div>
  );
};

export const NewActionChooserModal: React.FC<{
  activityBasePath: TypedPathTo<RigActivityConfigViewModel>;
  stageIndex: number;
  open: boolean;
  onDone: (viewModel: ActionViewModel<unknown>) => void;
  onClose: () => void;
}> = ({ activityBasePath, stageIndex, open, onDone, onClose }) => {
  const { user } = useSelf();
  const { t } = useTranslation();
  const { values } = useFormikContext<RigActivitiesFormViewModel>();
  const [activeSubmenu, setActiveSubmenu] = useState<SubmenuData | undefined>();
  const activityConfig = lodashGet(values, activityBasePath.$path);

  const submenus = useMemo(
    () =>
      listSubmenuSections(
        t,
        user,
        values.rigEffectiveConfig,
        activityConfig,
        stageIndex
      ),
    [t, user, values.rigEffectiveConfig, activityConfig, stageIndex]
  );

  const handleDone = useCallback(
    (viewModel: ActionViewModel<unknown>) => {
      setActiveSubmenu(undefined);
      onDone(viewModel);
    },
    [onDone]
  );

  const handleClose = useCallback(() => {
    setActiveSubmenu(undefined);
    onClose();
  }, [onClose]);

  let dialogTitle: string;
  let dialogSubtitle: string;
  let dialogBody: JSX.Element;

  if (activeSubmenu) {
    dialogTitle = activeSubmenu.title;
    dialogSubtitle = activeSubmenu.subtitle;
    dialogBody = (
      <NewActionChooserSubmenu
        stageIndex={stageIndex}
        submenu={activeSubmenu}
        onDone={handleDone}
        activityBasePath={activityBasePath}
      />
    );
  } else {
    dialogTitle = t(
      "views.autotractor.autonomousActivities.action.newMenu.mainTitle"
    );
    dialogSubtitle = t(
      "views.autotractor.autonomousActivities.action.newMenu.mainSubtitle"
    );
    dialogBody = (
      <NewActionChooserTopLevelMenu
        submenus={submenus}
        onOpenSubmenu={(submenu) => setActiveSubmenu(submenu)}
      />
    );
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        {activeSubmenu && (
          <Button
            startIcon={<MenuArrowLeftIcon />}
            variant="outlined"
            color="secondary"
            className="mb-4"
            onClick={() => setActiveSubmenu(undefined)}
          >
            {t("utils.actions.backShort")}
          </Button>
        )}
        <Typography variant="h5">{dialogTitle}</Typography>
        <Typography variant="subtitle1">{dialogSubtitle}</Typography>
        <Divider />
      </DialogTitle>
      <DialogContent>{dialogBody}</DialogContent>
    </Dialog>
  );
};
